/**
 * CalendarDialogs
 *
 * Renders all dialog/overlay components used by the calendar main page.
 * Each dialog receives its own open state and handlers via props.
 */

import type {
  CalendarEventInput,
  EventInstance,
  QuickEventDraft,
  RecurrenceRule,
  CalendarSettings,
  CalendarSet,
} from '../types';
import type {
  SchedulableTaskDetail,
  AiScheduleSuggestion,
  CalendarEventTemplateDetail,
  CalendarEventTemplateInput,
  FocusTimeSettingsDetail,
  FocusTimeSettingsInput,
  CalendarCollectionDetail,
  SchedulableTaskInput,
  SchedulingLinkDetail,
  SchedulingLinkInput,
  BookingDetail,
} from '@/lib/api';

import { EventDialog } from './EventDialog';
import type { EventDialogSettings } from './EventDialog';
import { QuickEventDialog } from './QuickEventDialog';
import { EventDetailDialog } from './EventDetailDialog';
import { CalendarFormDialog } from './CalendarFormDialog';
import { ImportDialog } from './ImportDialog';
import { SettingsSheet } from './SettingsSheet';
import { FeaturesDialog } from './FeaturesDialog';
import { CommandPalette } from './CommandPalette';
import { ShortcutHelpOverlay } from './ShortcutHelpOverlay';
import { CalendarSetManager } from './CalendarSetManager';
import { TemplateManagerDialog } from './TemplateManagerDialog';
import { ShareCalendarDialog } from './ShareCalendarDialog';
import { PublishFeedDialog } from './PublishFeedDialog';
import { FindTimeDialog } from './FindTimeDialog';
import { SchedulingLinksManager } from './SchedulingLinksManager';
import { TaskDialog } from './TaskDialog';
import { ScheduleSuggestionsDialog } from './ScheduleSuggestionsDialog';
import { DailyPlanningWizard } from './DailyPlanningWizard';
import { EveningReviewWizard } from './EveningReviewWizard';
import { FocusTimeDialog } from './FocusTimeDialog';
import { SubscribeDialog } from './SubscribeDialog';

import type { CalendarView, RecurrenceEditScope } from '../types';

export interface CalendarDialogsProps {
  // Event dialog
  eventDialogOpen: boolean;
  onCloseEventDialog: () => void;
  onSaveEvent: (input: CalendarEventInput & { rruleObj?: RecurrenceRule | null }) => Promise<void>;
  calendars: CalendarCollectionDetail[];
  editEvent: EventInstance | null;
  initialEventData: Partial<CalendarEventInput>;
  templates: CalendarEventTemplateDetail[];
  onSaveAsTemplate?: (name: string) => void;
  eventDialogSettings: EventDialogSettings;
  visibleEvents: EventInstance[];

  // Quick event dialog
  quickEventOpen: boolean;
  quickEventDraft: QuickEventDraft;
  onCloseQuickEvent: () => void;
  onQuickSave: (title: string) => Promise<void>;
  onQuickExpand: (parsedData?: Partial<CalendarEventInput>) => void;

  // Event detail dialog
  detailDialogOpen: boolean;
  selectedEvent: EventInstance | null;
  onCloseDetailDialog: () => void;
  onEditEvent: (event: EventInstance) => void;
  onDeleteEvent: (event: EventInstance, scope: RecurrenceEditScope) => Promise<void>;

  // Calendar form dialog
  calendarFormOpen: boolean;
  onCloseCalendarForm: () => void;
  onSaveCalendar: (input: { name: string; description?: string; color?: string }) => Promise<void>;

  // Import dialog
  importDialogOpen: boolean;
  onCloseImport: () => void;
  onImportComplete: () => void;
  onCreateCalendar: () => void;

  // Settings sheet
  settingsOpen: boolean;
  onCloseSettings: () => void;
  // Features dialog — the on/off switches for the toolbar's optional actions
  featuresOpen: boolean;
  onCloseFeatures: () => void;
  settings: CalendarSettings;
  onUpdateSettings: (update: Partial<CalendarSettings>) => void;
  focusTimeSettings?: FocusTimeSettingsDetail | null;
  onUpdateFocusTime?: (input: FocusTimeSettingsInput) => Promise<unknown>;
  onGenerateFocusBlocks?: () => Promise<number>;
  onRegenerateFocusBlocks?: () => Promise<number>;
  onClearFocusBlocks?: () => Promise<number>;

  // Command palette
  commandPaletteOpen: boolean;
  onCloseCommandPalette: () => void;
  onNewEvent: () => void;
  onToday: () => void;
  onPrev: () => void;
  onNext: () => void;
  onViewChange: (v: CalendarView) => void;
  onOpenSettings: () => void;
  onOpenImport: () => void;
  onGoToDate: (date: Date) => void;
  calendarSets: CalendarSet[];
  onActivateSet: (id: string) => void;
  onNewEventFromTemplate: (template: CalendarEventTemplateDetail) => void;
  onManageTemplates: () => void;

  // Shortcut help
  shortcutHelpOpen: boolean;
  onCloseShortcutHelp: () => void;

  // Calendar set manager
  calendarSetManagerOpen: boolean;
  onCloseCalendarSetManager: () => void;
  onCreateSet: (name: string, calendarIds: string[]) => void;
  onRenameSet: (id: string, name: string) => void;
  onDeleteSet: (id: string) => void;
  onUpdateSetCalendars: (id: string, calendarIds: string[]) => void;

  // Template manager
  templateManagerOpen: boolean;
  onCloseTemplateManager: () => void;
  onCreateTemplate: (input: CalendarEventTemplateInput) => Promise<unknown>;
  onUpdateTemplate: (id: string, input: Partial<CalendarEventTemplateInput>) => Promise<unknown>;
  onDeleteTemplate: (id: string) => Promise<void>;

  // Share calendar dialog
  shareDialogOpen: boolean;
  onCloseShareDialog: () => void;
  shareCalendarTarget: string | null;
  publishFeedDialogOpen: boolean;
  onClosePublishFeedDialog: () => void;
  publishFeedTarget: string | null;

  // Find time dialog
  findTimeOpen: boolean;
  onCloseFindTime: () => void;
  onFindTimeSelectSlot: (startAt: string, endAt: string) => void;

  // Scheduling links manager
  schedulingLinksOpen: boolean;
  onCloseSchedulingLinks: () => void;
  schedulingLinks: SchedulingLinkDetail[];
  onCreateLink: (input: SchedulingLinkInput) => Promise<SchedulingLinkDetail>;
  onUpdateLink: (id: string, input: Partial<SchedulingLinkInput>) => Promise<SchedulingLinkDetail>;
  onDeleteLink: (id: string) => Promise<void>;
  onFetchBookings: (linkId: string, status?: string) => Promise<BookingDetail[]>;
  onCancelBooking: (linkId: string, bookingId: string) => Promise<void>;

  // Task dialog
  taskDialogOpen: boolean;
  onCloseTaskDialog: () => void;
  onSaveTask: (input: SchedulableTaskInput) => Promise<void>;
  editTaskTarget: SchedulableTaskDetail | null;

  // Schedule suggestions dialog
  suggestionsDialogOpen: boolean;
  onCloseSuggestions: () => void;
  suggestionsTask: SchedulableTaskDetail | null;
  suggestions: AiScheduleSuggestion[];
  suggestionsLoading: boolean;
  onAcceptSuggestion: (index: number) => Promise<void>;

  // Daily planning wizard
  dailyPlanningOpen: boolean;
  onCloseDailyPlanning: () => void;
  dailyPlanning: {
    yesterdayEvents: EventInstance[];
    todayEvents: EventInstance[];
    incompleteTasks: SchedulableTaskDetail[];
    unscheduledTasks: SchedulableTaskDetail[];
    availableHours: number;
    scheduledHours: number;
    todayPriorities: string[];
    setTodayPriorities: (priorities: string[]) => void;
    dismissMorningWizard: () => void;
  };
  onCompleteTask: (taskId: string) => Promise<void>;
  onScheduleTaskToTime: (taskId: string, hour: number, durationMinutes: number) => Promise<void>;

  // Subscribe dialog
  subscribeDialogOpen: boolean;
  onCloseSubscribe: () => void;
  onSubscribe: (feedUrl: string, name?: string) => Promise<void>;

  // Evening review wizard
  eveningReviewOpen: boolean;
  onCloseEveningReview: () => void;
  eveningReviewDailyPlanning: {
    todayEvents: EventInstance[];
    todayPriorities: string[];
    saveEveningReview: (reflection: string, completedCount: number, plannedCount: number) => void;
    dismissEveningReview: () => void;
  };
  tasks: SchedulableTaskDetail[];

  // Focus time dialog
  focusTimeDialogOpen: boolean;
  onCloseFocusTimeDialog: () => void;
  onOpenSettingsFromFocusTime: () => void;
}

export function CalendarDialogs(props: CalendarDialogsProps) {
  return (
    <>
      <EventDialog
        open={props.eventDialogOpen}
        onClose={props.onCloseEventDialog}
        onSave={props.onSaveEvent}
        calendars={props.calendars}
        defaultCalendarId={props.eventDialogSettings.defaultCalendarId}
        initialData={props.initialEventData}
        editEvent={props.editEvent}
        templates={props.templates}
        onSaveAsTemplate={props.onSaveAsTemplate}
        settings={props.eventDialogSettings}
        existingEvents={props.visibleEvents}
      />

      <QuickEventDialog
        open={props.quickEventOpen}
        draft={props.quickEventDraft}
        onClose={props.onCloseQuickEvent}
        onSave={props.onQuickSave}
        onExpand={props.onQuickExpand}
        templates={props.templates}
        timeFormat={props.settings.timeFormat}
        existingEvents={props.visibleEvents}
        showConflictWarnings={props.settings.showConflictWarnings}
        allowDoubleBooking={props.settings.allowDoubleBooking}
      />

      <EventDetailDialog
        open={props.detailDialogOpen}
        event={props.selectedEvent}
        onClose={props.onCloseDetailDialog}
        onEdit={props.onEditEvent}
        onDelete={props.onDeleteEvent}
        timeFormat={props.settings.timeFormat}
      />

      <CalendarFormDialog
        open={props.calendarFormOpen}
        onClose={props.onCloseCalendarForm}
        onSave={props.onSaveCalendar}
      />

      <ImportDialog
        open={props.importDialogOpen}
        onClose={props.onCloseImport}
        calendars={props.calendars}
        onImportComplete={props.onImportComplete}
        onCreateCalendar={props.onCreateCalendar}
      />

      <SettingsSheet
        open={props.settingsOpen}
        onClose={props.onCloseSettings}
        settings={props.settings}
        onUpdateSettings={props.onUpdateSettings}
        focusTimeSettings={props.focusTimeSettings}
        onUpdateFocusTime={props.onUpdateFocusTime}
        onGenerateFocusBlocks={props.onGenerateFocusBlocks}
        onRegenerateFocusBlocks={props.onRegenerateFocusBlocks}
        onClearFocusBlocks={props.onClearFocusBlocks}
        calendars={props.calendars}
      />

      <FeaturesDialog
        open={props.featuresOpen}
        onClose={props.onCloseFeatures}
        settings={props.settings}
        onUpdateSettings={props.onUpdateSettings}
      />

      <CommandPalette
        open={props.commandPaletteOpen}
        onClose={props.onCloseCommandPalette}
        onNewEvent={props.onNewEvent}
        onToday={props.onToday}
        onPrev={props.onPrev}
        onNext={props.onNext}
        onViewChange={props.onViewChange}
        onSettings={props.onOpenSettings}
        onImport={props.onOpenImport}
        onGoToDate={props.onGoToDate}
        calendarSets={props.calendarSets}
        onActivateSet={props.onActivateSet}
        templates={props.templates}
        onNewEventFromTemplate={props.onNewEventFromTemplate}
        onManageTemplates={props.onManageTemplates}
      />

      <ShortcutHelpOverlay open={props.shortcutHelpOpen} onClose={props.onCloseShortcutHelp} />

      <CalendarSetManager
        open={props.calendarSetManagerOpen}
        onClose={props.onCloseCalendarSetManager}
        calendarSets={props.calendarSets}
        calendars={props.calendars}
        onCreateSet={props.onCreateSet}
        onRenameSet={props.onRenameSet}
        onDeleteSet={props.onDeleteSet}
        onUpdateSetCalendars={props.onUpdateSetCalendars}
      />

      <TemplateManagerDialog
        open={props.templateManagerOpen}
        onClose={props.onCloseTemplateManager}
        templates={props.templates}
        calendars={props.calendars}
        onCreateTemplate={props.onCreateTemplate}
        onUpdateTemplate={props.onUpdateTemplate}
        onDeleteTemplate={props.onDeleteTemplate}
      />

      <ShareCalendarDialog
        open={props.shareDialogOpen}
        onClose={props.onCloseShareDialog}
        calendarId={props.shareCalendarTarget}
        calendarName={props.calendars.find((c) => c.id === props.shareCalendarTarget)?.name}
      />

      <PublishFeedDialog
        open={props.publishFeedDialogOpen}
        onClose={props.onClosePublishFeedDialog}
        calendarId={props.publishFeedTarget}
        calendarName={props.calendars.find((c) => c.id === props.publishFeedTarget)?.name}
      />

      <FindTimeDialog
        open={props.findTimeOpen}
        onClose={props.onCloseFindTime}
        onSelectSlot={props.onFindTimeSelectSlot}
        timeFormat={props.settings.timeFormat}
      />

      <SchedulingLinksManager
        open={props.schedulingLinksOpen}
        onClose={props.onCloseSchedulingLinks}
        schedulingLinks={props.schedulingLinks}
        calendars={props.calendars}
        onCreateLink={props.onCreateLink}
        onUpdateLink={props.onUpdateLink}
        onDeleteLink={props.onDeleteLink}
        onFetchBookings={props.onFetchBookings}
        onCancelBooking={props.onCancelBooking}
        timeFormat={props.settings.timeFormat}
        defaultAvailabilityWindows={props.settings.defaultAvailabilityWindows}
        defaultBookingConfirmationMessage={props.settings.defaultBookingConfirmationMessage}
      />

      <TaskDialog
        open={props.taskDialogOpen}
        onClose={props.onCloseTaskDialog}
        onSave={props.onSaveTask}
        calendars={props.calendars}
        editTask={props.editTaskTarget}
        defaultTaskDeadlineOffset={props.settings.defaultTaskDeadlineOffset}
      />

      <ScheduleSuggestionsDialog
        open={props.suggestionsDialogOpen}
        onClose={props.onCloseSuggestions}
        task={props.suggestionsTask}
        suggestions={props.suggestions}
        loading={props.suggestionsLoading}
        onAccept={props.onAcceptSuggestion}
        timeFormat={props.settings.timeFormat}
      />

      <DailyPlanningWizard
        open={props.dailyPlanningOpen}
        onClose={props.onCloseDailyPlanning}
        yesterdayEvents={props.dailyPlanning.yesterdayEvents}
        todayEvents={props.dailyPlanning.todayEvents}
        incompleteTasks={props.dailyPlanning.incompleteTasks}
        unscheduledTasks={props.dailyPlanning.unscheduledTasks}
        availableHours={props.dailyPlanning.availableHours}
        scheduledHours={props.dailyPlanning.scheduledHours}
        todayPriorities={props.dailyPlanning.todayPriorities}
        setTodayPriorities={props.dailyPlanning.setTodayPriorities}
        onCompleteTask={props.onCompleteTask}
        onScheduleTask={props.onScheduleTaskToTime}
        workingHoursStart={props.settings.workingHoursStart}
        workingHoursEnd={props.settings.workingHoursEnd}
        onDismiss={props.dailyPlanning.dismissMorningWizard}
        timeFormat={props.settings.timeFormat}
        stepToggles={{
          showReviewYesterday: props.settings.showReviewYesterday ?? true,
          showTodaysSchedule: props.settings.showTodaysSchedule ?? true,
          showPlanYourDay: props.settings.showPlanYourDay ?? true,
          showSetYourFocus: props.settings.showSetYourFocus ?? true,
        }}
        onUpdateSettings={props.onUpdateSettings}
      />

      <SubscribeDialog
        open={props.subscribeDialogOpen}
        onClose={props.onCloseSubscribe}
        onSubscribe={props.onSubscribe}
      />

      <EveningReviewWizard
        open={props.eveningReviewOpen}
        onClose={props.onCloseEveningReview}
        todayEvents={props.eveningReviewDailyPlanning.todayEvents}
        tasks={props.tasks}
        todayPriorities={props.eveningReviewDailyPlanning.todayPriorities}
        onCompleteTask={props.onCompleteTask}
        onScheduleTask={props.onScheduleTaskToTime}
        onSaveReview={props.eveningReviewDailyPlanning.saveEveningReview}
        timeFormat={props.settings.timeFormat}
        stepToggles={{
          showEveningDaySummary: props.settings.showEveningDaySummary ?? true,
          showEveningReschedule: props.settings.showEveningReschedule ?? true,
          showEveningReflection: props.settings.showEveningReflection ?? true,
        }}
        onUpdateSettings={props.onUpdateSettings}
      />

      {props.focusTimeSettings !== undefined &&
        props.onUpdateFocusTime &&
        props.onGenerateFocusBlocks &&
        props.onRegenerateFocusBlocks &&
        props.onClearFocusBlocks && (
          <FocusTimeDialog
            open={props.focusTimeDialogOpen}
            onClose={props.onCloseFocusTimeDialog}
            focusTimeSettings={props.focusTimeSettings ?? null}
            onUpdateFocusTime={props.onUpdateFocusTime}
            onGenerate={props.onGenerateFocusBlocks}
            onRegenerate={props.onRegenerateFocusBlocks}
            onClear={props.onClearFocusBlocks}
            onOpenSettings={props.onOpenSettingsFromFocusTime}
            timeFormat={props.settings.timeFormat}
          />
        )}
    </>
  );
}
